// The `pull_request` wire shape — one projection, consumed by
// `GET /v1/prs/:id`, `GET /v1/repos/:o/:r/prs` and the MCP `pr` verb's
// `list`/`get`.
//
// The two hand-written copies this replaces (`projectPr` in
// `apps/api/src/routes/prs.ts`, `maskPr` in `packages/mcp/src/army/pr.ts`) had
// byte-identical bodies and disagreeing signatures: REST restated all twelve
// fields inline, MCP returned `Record<string, unknown>` and so published
// nothing a client could check.

import { z } from 'zod';
import { defineResource } from './resource';

/**
 * The camelCase shape the projection reads. `PullRequest`
 * (`entities/issue.ts`) satisfies it structurally; `pull-request.test.ts`
 * proves that by serializing a parsed one.
 */
export type PullRequestEntity = {
  id: string;
  repoId: string;
  githubNumber: number;
  githubId: number;
  title: string;
  authorLogin: string | null;
  state: string;
  ciStatus: string;
  reviewersSeen: string[];
  readySignals: string[];
  mergeDecision: string | null;
  lastSessionId: string | null;
  /** The PR's current head (plan 101 M2) — what an approve/skip is OF. Null before a push was seen. */
  headSha: string | null;
};

/** The published body. Field names and nullability are the existing contract. */
export const PullRequestWireSchema = z.object({
  id: z.string(),
  repo_id: z.string(),
  github_number: z.number(),
  github_id: z.number(),
  title: z.string(),
  author_login: z.string().nullable(),
  state: z.string(),
  ci_status: z.string(),
  reviewers_seen: z.array(z.string()),
  ready_signals: z.array(z.string()),
  merge_decision: z.string().nullable(),
  last_session_id: z.string().nullable(),
  /** The current head. `POST /v1/prs/:id/{approve,skip}-pending` takes it back as `head_sha`. */
  head_sha: z.string().nullable(),
});

/** What a client should type against for a PR body. */
export type PullRequestWire = z.infer<typeof PullRequestWireSchema>;

export const PullRequestResource = defineResource<PullRequestEntity, typeof PullRequestWireSchema>({
  schema: PullRequestWireSchema,
  serialize: (pr) => ({
    id: pr.id,
    repo_id: pr.repoId,
    github_number: pr.githubNumber,
    github_id: pr.githubId,
    title: pr.title,
    author_login: pr.authorLogin,
    state: pr.state,
    ci_status: pr.ciStatus,
    reviewers_seen: pr.reviewersSeen,
    ready_signals: pr.readySignals,
    merge_decision: pr.mergeDecision,
    last_session_id: pr.lastSessionId,
    head_sha: pr.headSha,
  }),
});
